/**
 * UI-3b Task 3 — ContactDrawer.
 *
 * The create/edit drawer for a customer contact (spec §3.4), hosted inside the
 * shared `ResourceDrawer` (`width="standard"`).
 *
 * Backend contract (apps/api/src/routes/contacts/schemas.ts)
 * ----------------------------------------------------------
 *   - POST  /contacts          body: { name, phoneE164, email?, notes? }
 *                              (CreateContactRequestSchema — email/notes are
 *                              `.optional()`, never null on create).
 *   - PATCH /contacts/:id      body: { name?, email?, notes? }
 *                              (UpdateContactRequestSchema — phone is NOT
 *                              updatable; email/notes are `.nullable().optional()`,
 *                              so they may be sent as `null` to clear them).
 *
 * Save ownership
 * --------------
 * The props interface — `{ open, contact, onClose, onSaved }` — has no
 * `onSave`. The drawer itself performs the API call, then calls `onSaved()`
 * followed by `onClose()`.
 *
 *   - create: POST  /contacts            (full body)
 *   - update: PATCH /contacts/:id        (only changed fields — diff vs. the
 *                                         original contact; if nothing changed,
 *                                         the PATCH is skipped as a no-op)
 *
 * The drawer owns its own `<ZodForm>`; `ResourceDrawer`'s Save button drives
 * the validated submit via `formRef.current.requestSubmit()`.
 *
 * Phone field
 * -----------
 * Phone is editable in CREATE mode and validated as E.164. In EDIT mode the
 * backend rejects phone changes, so it is rendered READ-ONLY (disabled input
 * showing `contact.phoneE164`) and is not part of the validated schema.
 *
 * Notes counter
 * -------------
 * A live `current / 8192` display under the textarea, computed from the
 * watched `notes` field value.
 *
 * Remount per contact
 * -------------------
 * `ZodForm` mount-initializes from `defaultValues` and does not re-sync. The
 * inner form is therefore given a `key` of `contact?.id ?? 'new'` so opening a
 * different contact — or switching between create and edit — fully remounts it.
 */
import { useRef, useState, type JSX } from 'react'
import { z } from 'zod'
import { useFormContext } from 'react-hook-form'
import ResourceDrawer from '../ResourceDrawer.js'
import { ZodForm, FormText, FormTextarea } from '../../forms/index.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import { isValidE164 } from '../../../utils/e164.js'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface Contact {
  id: string
  name: string
  phoneE164: string
  email: string | null
  notes: string | null
  consentStatus?: 'GRANTED' | 'REVOKED' | 'PENDING'
  createdAt: string
  updatedAt: string
}

export interface ContactDrawerProps {
  open: boolean
  /** The contact being edited; `null` = create mode. */
  contact: Contact | null
  onClose: () => void
  /** Called after a successful save so the parent can refetch + close. */
  onSaved: () => void
}

const NOTES_MAX = 8192

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

const nameField = z.string().trim().min(1, 'Name is required').max(128, 'Name is too long')
// Empty string is treated as "not provided" for the optional fields.
const emailField = z
  .string()
  .trim()
  .refine((v) => v === '' || z.string().email().safeParse(v).success, {
    message: 'Must be a valid email address',
  })
const notesField = z.string().max(NOTES_MAX, `Notes must be ${NOTES_MAX} characters or fewer`)

/** CREATE — phone is part of the form and E.164-validated. */
const CreateContactSchema = z.object({
  name: nameField,
  phoneE164: z
    .string()
    .trim()
    .min(1, 'Phone number is required')
    .refine(isValidE164, {
      message: 'Enter a valid phone number in E.164 format (e.g. +15551234567)',
    }),
  email: emailField,
  notes: notesField,
})

/** EDIT — phone is read-only and excluded from validation. */
const EditContactSchema = z.object({
  name: nameField,
  email: emailField,
  notes: notesField,
})

interface ContactFormValues {
  name: string
  phoneE164?: string
  email: string
  notes: string
}

// ---------------------------------------------------------------------------
// Notes character counter — reads the live `notes` value from form context.
// ---------------------------------------------------------------------------

function NotesCounter(): JSX.Element {
  const { watch } = useFormContext()
  const value = watch('notes')
  const length = typeof value === 'string' ? value.length : 0
  return (
    <small
      className={length > NOTES_MAX ? 'text-red-600' : 'text-gray-500'}
      aria-live="polite"
    >
      {length} / {NOTES_MAX}
    </small>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ContactDrawer({
  open,
  contact,
  onClose,
  onSaved,
}: ContactDrawerProps): JSX.Element {
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)

  // Consent states
  const [recordConsent, setRecordConsent] = useState(false)
  const [consentChannel, setConsentChannel] = useState<'VOICE' | 'SMS' | 'EMAIL' | 'WHATSAPP'>('VOICE')
  const [consentEvidenceType, setConsentEvidenceType] = useState<'recording' | 'form' | 'sms_opt_in' | 'other'>('form')
  const [consentEvidenceUrl, setConsentEvidenceUrl] = useState('')
  const [withdrawing, setWithdrawing] = useState(false)

  const isEdit = contact !== null
  /** Remount key — changes when a different contact (or create mode) opens. */
  const contentKey = contact?.id ?? 'new'

  const defaultValues: ContactFormValues = {
    name: contact?.name ?? '',
    ...(isEdit ? {} : { phoneE164: '' }),
    email: contact?.email ?? '',
    notes: contact?.notes ?? '',
  }

  // --- Save flow -----------------------------------------------------------

  async function handleSubmit(values: ContactFormValues): Promise<void> {
    setSaving(true)
    try {
      if (contact === null) {
        const payload: Record<string, unknown> = {
          name: values.name,
          phoneE164: values.phoneE164,
          email: values.email || undefined,
          notes: values.notes || undefined,
        }
        if (recordConsent) {
          payload.consent = {
            channel: consentChannel,
            evidenceType: consentEvidenceType,
            evidenceUrl: consentEvidenceUrl.trim() || undefined,
          }
        }
        await api.post('/contacts', payload)
        toast.success('Contact created')
      } else {
        // Diff vs. the original — send only changed fields. Empty string clears
        // the nullable email/notes columns (sent as `null`).
        const body: Record<string, unknown> = {}
        if (values.name !== contact.name) body.name = values.name
        if (values.email !== (contact.email ?? '')) {
          body.email = values.email || null
        }
        if (values.notes !== (contact.notes ?? '')) {
          body.notes = values.notes || null
        }
        // No-op guard: if nothing changed, skip the PATCH entirely — the backend
        // rejects an empty body with 400 ("at least one field must be provided"),
        // which would surface a confusing error toast for a benign action.
        if (Object.keys(body).length === 0) {
          onSaved()
          onClose()
          return
        }
        await api.patch(`/contacts/${contact.id}`, body)
        toast.success('Contact updated')
      }
      onSaved()
      onClose()
    } catch (err) {
      // Keep the drawer open on failure; surface a readable error.
      toast.error(err instanceof Error ? err.message : 'Failed to save contact.')
    } finally {
      setSaving(false)
    }
  }

  async function handleWithdrawConsent(): Promise<void> {
    if (!contact) return
    setWithdrawing(true)
    try {
      await api.post('/admin/compliance/consent-records/withdraw', {
        contactId: contact.id,
        reason: 'Withdrawn from Contact Drawer',
      })
      toast.success('Consent withdrawn and contact added to DNC list')
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to withdraw consent')
    } finally {
      setWithdrawing(false)
    }
  }

  // --- Render --------------------------------------------------------------

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      formRef={formRef}
      saving={saving}
      width="standard"
      title={contact ? `Edit Contact: ${contact.name}` : 'New Contact'}
    >
      <ZodForm
        key={contentKey}
        schema={isEdit ? EditContactSchema : CreateContactSchema}
        defaultValues={defaultValues}
        onSubmit={(data) => handleSubmit(data as ContactFormValues)}
        formRef={formRef}
      >
        <FormText name="name" label="Name" required autoFocus placeholder="Contact name" />

        {isEdit ? (
          // Phone is not updatable — render it read-only, outside the schema.
          <div className="flex flex-col gap-1 mb-3">
            <label htmlFor="contact-phone-readonly" className="text-sm font-medium">
              Phone
            </label>
            <input
              id="contact-phone-readonly"
              type="tel"
              value={contact.phoneE164}
              disabled
              readOnly
              className="border rounded px-2 py-1 bg-gray-100 text-gray-600"
            />
            <small className="text-gray-500">Phone numbers cannot be changed.</small>
          </div>
        ) : (
          <FormText
            name="phoneE164"
            label="Phone"
            type="tel"
            required
            placeholder="+15551234567"
            helpText="E.164 format — a leading + and country code."
          />
        )}

        {isEdit && (
          <div className="flex items-center justify-between p-3 rounded bg-gray-50 border border-gray-200 mb-3" data-testid="contact-consent-status-badge">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                Consent Status
              </span>
              <span className={`inline-block px-2 py-0.5 mt-1 rounded text-xs font-semibold ${
                contact.consentStatus === 'GRANTED' ? 'bg-green-100 text-green-800' :
                contact.consentStatus === 'REVOKED' ? 'bg-red-100 text-red-800' :
                'bg-amber-100 text-amber-800'
              }`}>
                {contact.consentStatus ?? 'PENDING'}
              </span>
            </div>
            {contact.consentStatus === 'GRANTED' && (
              <button
                type="button"
                onClick={() => { void handleWithdrawConsent() }}
                disabled={withdrawing}
                className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-300 rounded transition-colors disabled:opacity-50"
                data-testid="contact-withdraw-consent-btn"
              >
                {withdrawing ? 'Withdrawing…' : 'Withdraw Consent'}
              </button>
            )}
          </div>
        )}

        <FormText name="email" label="Email" type="email" placeholder="name@example.com" />

        <FormTextarea name="notes" label="Notes" rows={4} placeholder="Optional notes" />
        <div className="-mt-2 mb-3 flex justify-end">
          <NotesCounter />
        </div>

        {!isEdit && (
          <div className="rounded border border-gray-200 bg-gray-50 p-3 mb-3 flex flex-col gap-2.5" data-testid="contact-consent-section">
            <div className="flex items-center gap-2">
              <input
                id="contact-record-consent"
                type="checkbox"
                checked={recordConsent}
                onChange={(e) => setRecordConsent(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="contact-record-consent-checkbox"
              />
              <label htmlFor="contact-record-consent" className="text-sm font-medium text-gray-800 cursor-pointer">
                Record initial consent for this contact (TCPA)
              </label>
            </div>
            {recordConsent && (
              <div className="flex flex-col gap-2 pt-2 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="contact-consent-channel" className="block text-xs font-medium text-gray-700 mb-1">
                      Channel
                    </label>
                    <select
                      id="contact-consent-channel"
                      value={consentChannel}
                      onChange={(e) => setConsentChannel(e.target.value as typeof consentChannel)}
                      className="w-full border rounded px-2 py-1 text-sm bg-white"
                      data-testid="contact-consent-channel-select"
                    >
                      <option value="VOICE">VOICE</option>
                      <option value="SMS">SMS</option>
                      <option value="EMAIL">EMAIL</option>
                      <option value="WHATSAPP">WHATSAPP</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-consent-evidence-type" className="block text-xs font-medium text-gray-700 mb-1">
                      Evidence Type
                    </label>
                    <select
                      id="contact-consent-evidence-type"
                      value={consentEvidenceType}
                      onChange={(e) => setConsentEvidenceType(e.target.value as typeof consentEvidenceType)}
                      className="w-full border rounded px-2 py-1 text-sm bg-white"
                      data-testid="contact-consent-evidence-type-select"
                    >
                      <option value="form">form</option>
                      <option value="recording">recording</option>
                      <option value="sms_opt_in">sms_opt_in</option>
                      <option value="other">other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-consent-evidence-url" className="block text-xs font-medium text-gray-700 mb-1">
                    Evidence URL (optional)
                  </label>
                  <input
                    id="contact-consent-evidence-url"
                    type="url"
                    value={consentEvidenceUrl}
                    onChange={(e) => setConsentEvidenceUrl(e.target.value)}
                    placeholder="https://example.com/recordings/consent.mp3"
                    className="w-full border rounded px-2 py-1 text-sm bg-white"
                    data-testid="contact-consent-evidence-url-input"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </ZodForm>
    </ResourceDrawer>
  )
}
